import { Component } from '@theme/component';

/**
 * A custom element that displays a live countdown to a target epoch.
 *
 * The block server-renders the initial HH:MM:SS so there is no flash before
 * this script upgrades the element. Once connected, the element re-computes
 * the remaining time every second and writes it into the `digits` ref. When
 * the remaining time reaches zero the display freezes at 00:00:00 and the
 * interval is cleared.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} digits - The element holding the HH:MM:SS text.
 *
 * @extends Component<Refs>
 */
export class CountdownTimer extends Component {
  requiredRefs = ['digits'];

  /** @type {number | null} */
  #intervalId = null;

  /** @type {number | null} */
  #endEpoch = null;

  connectedCallback() {
    super.connectedCallback();

    const raw = Number(this.dataset.end);
    if (!Number.isFinite(raw) || raw <= 0) return;
    this.#endEpoch = raw;

    this.#update();
    if (this.#intervalId === null && this.#endEpoch !== null) {
      this.#intervalId = window.setInterval(() => this.#update(), 1000);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.#intervalId !== null) {
      window.clearInterval(this.#intervalId);
      this.#intervalId = null;
    }
  }

  #update() {
    if (this.#endEpoch === null) return;
    const remaining = Math.max(0, this.#endEpoch - Math.floor(Date.now() / 1000));

    const hours = Math.floor(remaining / 3600);
    const minutes = Math.floor((remaining % 3600) / 60);
    const seconds = remaining % 60;

    const hh = hours < 10 ? `0${hours}` : `${hours}`;
    const mm = minutes < 10 ? `0${minutes}` : `${minutes}`;
    const ss = seconds < 10 ? `0${seconds}` : `${seconds}`;

    this.refs.digits.textContent = `${hh}:${mm}:${ss}`;

    if (remaining <= 0 && this.#intervalId !== null) {
      window.clearInterval(this.#intervalId);
      this.#intervalId = null;
    }
  }
}

if (!customElements.get('countdown-timer')) {
  customElements.define('countdown-timer', CountdownTimer);
}
